/**
 * 個人ショット分析プロファイルパネル（自前・GPL非依存）。
 *
 * ロビーの行タップで開くオーバーレイ。サーバーから取得した AnalyticsResponse
 * を 4 タブ（総合・精度・ポケット別・推移）で表示する。
 *
 * 設計方針:
 *   - DOM は完全に自前で構築（controls.ts の肥大を避けるため別ファイル）。
 *   - CSS は index.html に集約（既存 .pool-* と同名前空間で衝突しない）。
 *   - グラフは自前 SVG（依存追加なし・GPL封じ込めにも整合）。
 *   - データ取得は呼び出し側 (main.ts) が行い、open(name, data) に渡す。
 */

import type {
  AnalyticsResponse,
  AnalyticsTotals,
  AnalyticsBucket,
} from '../net/lobby.js';

export interface ProfilePanelApi {
  /** プロファイルを開く。data=null は「未取得 or データなし」を表示。
   *  lastSeen/online は最終アクセス行（一般タブ）に表示する。
   *  lastSeen: epoch ms（0=未記録）／online: true なら「オンライン」と表示し時刻は出さない。
   *  2026-06-17: 旧ロビー Last online 列の機能をプライバシー保護のため個人画面へ移設。 */
  open(
    name: string,
    data: AnalyticsResponse | null,
    lastSeen: number,
    online: boolean,
  ): void;
  /** ロード中表示。データ取得開始時に呼ぶ。 */
  openLoading(name: string, lastSeen: number, online: boolean): void;
  /** プロファイルを閉じる。 */
  close(): void;
}

type TabKey = 'general' | 'accuracy' | 'pockets' | 'trend';
type TrendRange = 'day' | 'week' | 'month' | 'year';

const TAB_LABELS: ReadonlyArray<readonly [TabKey, string]> = [
  ['general', '総合'],
  ['accuracy', '精度'],
  ['pockets', 'ポケット別'],
  ['trend', '推移'],
];

const POCKET_LABELS = ['NW', 'NE', 'SW', 'SE', 'N(side)', 'S(side)'] as const;

const FOUL_LABELS: Readonly<Record<string, string>> = {
  scratch: 'スクラッチ',
  noHit: '空振り',
  wrongBall: '誤球',
  noRail: 'ノークッション',
  objectBallOffTable: '盤外',
  illegalBreak: '反則ブレイク',
  doubleHit: '二度撞き',
};

const el = <T extends HTMLElement = HTMLElement>(
  tag: string,
  cls: string,
  parent?: HTMLElement,
): T => {
  const e = document.createElement(tag) as T;
  if (cls) e.className = cls;
  if (parent) parent.appendChild(e);
  return e;
};

/** 0〜100 の整数 % にフォーマット（n/d=0時は '-'）。 */
const pct = (n: number, d: number): string =>
  d <= 0 ? '-' : `${Math.round((n / d) * 100)}%`;

/** 小数1桁のフォーマット（NaN/負時は '-'）。 */
const num1 = (v: number): string =>
  Number.isFinite(v) && v >= 0 ? v.toFixed(1) : '-';

export const createProfilePanel = (): ProfilePanelApi => {
  const overlay = el('div', 'pool-profile-overlay', document.body);
  overlay.hidden = true;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  const panel = el('div', 'pool-profile-panel', overlay);
  // パネル内クリックでオーバーレイ閉じを止める（外周のみで閉じる）。
  panel.addEventListener('click', (ev) => ev.stopPropagation());
  const header = el('div', 'pool-profile-header', panel);
  const title = el<HTMLHeadingElement>('h2', 'pool-profile-title', header);
  const closeBtn = el<HTMLButtonElement>('button', 'pool-profile-close', header);
  closeBtn.type = 'button';
  closeBtn.textContent = '✕';
  closeBtn.setAttribute('aria-label', '閉じる');
  const tabs = el('div', 'pool-profile-tabs', panel);
  const body = el('div', 'pool-profile-body', panel);

  const tabBtns = new Map<TabKey, HTMLButtonElement>();
  let currentTab: TabKey = 'general';
  let currentData: AnalyticsResponse | null = null;
  let trendRange: TrendRange = 'week';
  // 最終アクセス（epoch ms・0=未記録）と在席状態。open()/openLoading() で更新する。
  // 2026-06-17: 旧ロビー Last online 列をプライバシー保護のため個人画面へ移設したもの。
  let currentLastSeen = 0;
  let currentOnline = false;

  for (const [key, label] of TAB_LABELS) {
    const b = el<HTMLButtonElement>('button', 'pool-profile-tab', tabs);
    b.type = 'button';
    b.dataset.tab = key;
    b.textContent = label;
    b.addEventListener('click', () => {
      currentTab = key;
      renderTabs();
      renderBody();
    });
    tabBtns.set(key, b);
  }

  const renderTabs = (): void => {
    tabBtns.forEach((b, key) => {
      b.classList.toggle('is-active', key === currentTab);
    });
  };

  const close = (): void => {
    overlay.hidden = true;
  };
  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (ev) => {
    if (ev.target === overlay) close();
  });

  const renderBody = (): void => {
    while (body.firstChild) body.removeChild(body.firstChild);
    // 最終アクセス行: 一般タブのみ表示（プロファイルの「いつ／在席」コンテキスト）。
    // 2026-06-17: 旧ロビー Last online 列の機能をここへ移設（プライバシー保護）。
    if (currentTab === 'general') {
      renderPresence(body, currentLastSeen, currentOnline);
    }
    if (!currentData) {
      const empty = el('div', 'pool-profile-empty', body);
      empty.textContent =
        'プロファイル情報がありません（対戦未実施 / サーバー接続なし）。';
      return;
    }
    // 信憑性確保のため、ソロプレイ（オートエイム等のガイド機能あり）は集計対象外。
    // 対戦データのみを集計している旨を毎タブの上部に明示する。
    const note = el('p', 'pool-profile-note', body);
    note.textContent = '※ 対戦データのみ集計（ソロは除外）';
    if (currentTab === 'general') renderGeneral(body, currentData.totals);
    else if (currentTab === 'accuracy') renderAccuracy(body, currentData.totals);
    else if (currentTab === 'pockets') renderPockets(body, currentData.totals);
    else renderTrend(body, currentData.daily);
  };

  // ---- タブ別レンダラ ----

  const renderGeneral = (parent: HTMLElement, t: AnalyticsTotals): void => {
    const grid = el('div', 'pool-profile-grid', parent);
    addStat(grid, '試合勝数', String(t.matchWins));
    addStat(grid, '試合敗数', String(t.matchLosses));
    addStat(grid, '試合勝率', pct(t.matchWins, t.matchWins + t.matchLosses));
    addStat(grid, 'ラック勝率', pct(t.rackWins, t.rackWins + t.rackLosses));
    addStat(grid, '総ショット数', String(t.shots));
    addStat(grid, 'ショット成功率', pct(t.success, t.shots));
    addStat(grid, 'ブレイク数', String(t.breaks));
    addStat(grid, '合法ブレイク率', pct(t.legalBreaks, t.breaks));
    addStat(
      grid,
      '平均ブレイク落球',
      t.breaks > 0 ? num1(t.breakPotted / t.breaks) : '-',
    );
    addStat(
      grid,
      '出し成功率(≤50°)',
      pct(t.nextLeaveUnder50, t.nextLeaveCount),
    );
    addStat(
      grid,
      '平均カット角(次球)',
      t.nextLeaveCount > 0 ? `${num1(t.cutAngleSumDeg / t.nextLeaveCount)}°` : '-',
    );
    addStat(
      grid,
      '平均手球移動',
      t.shots > 0 ? `${num1(t.cuePathSumM / t.shots)} m` : '-',
    );
  };

  const renderAccuracy = (parent: HTMLElement, t: AnalyticsTotals): void => {
    const grid = el('div', 'pool-profile-grid', parent);
    addStat(grid, '成功', String(t.success));
    addStat(grid, 'ミス', String(t.miss));
    addStat(grid, 'ファウル', String(t.foulShots));
    addStat(grid, '成功率', pct(t.success, t.shots));
    addStat(grid, 'ミス率', pct(t.miss, t.shots));
    addStat(grid, 'ファウル率', pct(t.foulShots, t.shots));
    // ファウル種別内訳
    const h = el('h3', 'pool-profile-h', parent);
    h.textContent = 'ファウル内訳';
    const table = el('div', 'pool-profile-table', parent);
    const kinds = Object.keys(t.foulsByKind).sort(
      (a, b) => (t.foulsByKind[b] ?? 0) - (t.foulsByKind[a] ?? 0),
    );
    if (kinds.length === 0) {
      const empty = el('div', 'pool-profile-row-empty', table);
      empty.textContent = '記録なし';
      return;
    }
    for (const k of kinds) {
      const row = el('div', 'pool-profile-row', table);
      const label = el('span', 'pool-profile-row-l', row);
      label.textContent = FOUL_LABELS[k] ?? k;
      const val = el('span', 'pool-profile-row-v', row);
      val.textContent = String(t.foulsByKind[k] ?? 0);
    }
  };

  const renderPockets = (parent: HTMLElement, t: AnalyticsTotals): void => {
    const pp = Array.isArray(t.pocketPotted) ? t.pocketPotted : [];
    const total = pp.reduce((s, v) => s + (v ?? 0), 0);
    const note = el('p', 'pool-profile-note', parent);
    note.textContent = `総落球: ${total}`;
    const table = el('div', 'pool-profile-table', parent);
    for (let i = 0; i < 6; i++) {
      const v = pp[i] ?? 0;
      const row = el('div', 'pool-profile-row', table);
      const label = el('span', 'pool-profile-row-l', row);
      label.textContent = POCKET_LABELS[i] ?? `P${i}`;
      const val = el('span', 'pool-profile-row-v', row);
      val.textContent = `${v}  (${pct(v, total)})`;
    }
    // 簡易バー
    const bars = el('div', 'pool-profile-bars', parent);
    const maxV = Math.max(1, ...pp);
    for (let i = 0; i < 6; i++) {
      const v = pp[i] ?? 0;
      const barRow = el('div', 'pool-profile-bar-row', bars);
      const barLabel = el('span', 'pool-profile-bar-l', barRow);
      barLabel.textContent = POCKET_LABELS[i] ?? `P${i}`;
      const barTrack = el('span', 'pool-profile-bar-track', barRow);
      const barFill = el('span', 'pool-profile-bar-fill', barTrack);
      barFill.style.width = `${Math.round((v / maxV) * 100)}%`;
      const barVal = el('span', 'pool-profile-bar-v', barRow);
      barVal.textContent = String(v);
    }
  };

  const renderTrend = (
    parent: HTMLElement,
    daily: readonly AnalyticsBucket[],
  ): void => {
    // 切替: 日 / 週 / 月 / 年
    const toolbar = el('div', 'pool-profile-trend-tabs', parent);
    const ranges: ReadonlyArray<readonly [TrendRange, string]> = [
      ['day', '日'],
      ['week', '週'],
      ['month', '月'],
      ['year', '年'],
    ];
    for (const [k, label] of ranges) {
      const b = el<HTMLButtonElement>('button', 'pool-profile-trend-tab', toolbar);
      b.type = 'button';
      b.textContent = label;
      b.classList.toggle('is-active', k === trendRange);
      b.addEventListener('click', () => {
        trendRange = k;
        renderBody();
      });
    }
    const aggregated = aggregateBuckets(daily, trendRange);
    if (aggregated.length === 0) {
      const empty = el('div', 'pool-profile-empty', parent);
      empty.textContent = '推移データなし（ショットを撞くと記録されます）。';
      return;
    }
    // SVG 折れ線（勝率＋成功率）
    const svg = buildTrendSvg(aggregated);
    parent.appendChild(svg);
    // 凡例＋直近値
    const legend = el('div', 'pool-profile-legend', parent);
    const winLeg = el('span', 'pool-profile-legend-item is-win', legend);
    winLeg.textContent = '■ 試合勝率';
    const accLeg = el('span', 'pool-profile-legend-item is-acc', legend);
    accLeg.textContent = '■ ショット成功率';
    // 集計テーブル（最新→古い順）
    const table = el('div', 'pool-profile-table', parent);
    for (let i = aggregated.length - 1; i >= 0; i--) {
      const b = aggregated[i]!;
      const row = el('div', 'pool-profile-row', table);
      const l = el('span', 'pool-profile-row-l', row);
      l.textContent = b.label;
      const v = el('span', 'pool-profile-row-v', row);
      v.textContent = `${b.shots} shots / 成功${pct(b.success, b.shots)} / 勝${b.matchWins}・敗${b.matchLosses}`;
    }
  };

  // ---- 公開 API ----

  const openLoading = (
    name: string,
    lastSeen: number,
    online: boolean,
  ): void => {
    currentData = null;
    currentLastSeen = lastSeen;
    currentOnline = online;
    title.textContent = name || '(名前未設定)';
    overlay.hidden = false;
    currentTab = 'general';
    renderTabs();
    while (body.firstChild) body.removeChild(body.firstChild);
    // 最終アクセス行: 読み込み中もコンテキスト情報として先に出す（取得失敗時も残る）。
    renderPresence(body, currentLastSeen, currentOnline);
    const loading = el('div', 'pool-profile-empty', body);
    loading.textContent = '読み込み中...';
  };

  const open = (
    name: string,
    data: AnalyticsResponse | null,
    lastSeen: number,
    online: boolean,
  ): void => {
    currentData = data;
    currentLastSeen = lastSeen;
    currentOnline = online;
    title.textContent = name || '(名前未設定)';
    overlay.hidden = false;
    if (!tabBtns.get(currentTab)?.classList.contains('is-active')) {
      currentTab = 'general';
    }
    renderTabs();
    renderBody();
  };

  return { open, openLoading, close };
};

// ---- ヘルパ ----

const addStat = (parent: HTMLElement, label: string, value: string): void => {
  const card = el('div', 'pool-profile-stat', parent);
  const l = el('div', 'pool-profile-stat-l', card);
  l.textContent = label;
  const v = el('div', 'pool-profile-stat-v', card);
  v.textContent = value;
};

/** 最終アクセス（lastSeen）と在席（online）を 1 行で描画する。
 *  2026-06-17: 旧ロビー Last online 列の機能を個人プロファイル画面へ移設したヘルパ。
 *    - online=true の時は「● オンライン」（時刻は出さない）。
 *    - online=false の時は「○ 最終アクセス: 5分前（2026/06/17 12:34）」のように相対＋絶対併記。
 *    - lastSeen<=0（未記録・新規ユーザ等）は「○ 最終アクセス: 記録なし」。
 *  describing the absolute timestamp inside parentheses lets the user judge intent without
 *  needing a tooltip (which mobile can't show). */
const renderPresence = (
  parent: HTMLElement,
  lastSeen: number,
  online: boolean,
): void => {
  const row = el('div', 'pool-profile-presence', parent);
  row.classList.toggle('is-online', online);
  el('span', 'pool-profile-presence-dot', row);
  const text = el('span', 'pool-profile-presence-text', row);
  if (online) {
    text.textContent = 'オンライン';
  } else if (lastSeen > 0) {
    text.textContent = `最終アクセス: ${formatLastOnlineRelative(lastSeen)}（${formatLastOnlineAbsolute(lastSeen)}）`;
  } else {
    text.textContent = '最終アクセス: 記録なし';
  }
};

/** 最終アクセスの相対表記（「今」「5分前」「2時間前」「3日前」「30日以上前」）。
 *  ロビー一覧時代の短縮表記（'5分'/'2時間'/'3日'）よりも、個人画面では情報量を取って
 *  「〜前」を付ける（横幅の制約が緩いため可読性優先）。 */
const formatLastOnlineRelative = (lastSeen: number): string => {
  if (lastSeen <= 0) return '記録なし';
  const diff = Date.now() - lastSeen;
  if (diff < 60_000) return '今';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}分前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}時間前`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}日前`;
  return '30日以上前';
};

/** 最終アクセスの絶対日時（ローカルロケール）。 */
const formatLastOnlineAbsolute = (lastSeen: number): string =>
  lastSeen > 0 ? new Date(lastSeen).toLocaleString() : '記録なし';

interface AggregatedBucket {
  readonly label: string;
  readonly shots: number;
  readonly success: number;
  readonly matchWins: number;
  readonly matchLosses: number;
}

/** 日次バケットを集計単位（日/週/月/年）でまとめる。
 *  日: そのまま (最大365件)
 *  週: ISO 週で集計（YYYY-W##）
 *  月: 月単位 (YYYY-MM)
 *  年: 年単位 (YYYY)
 *  matchWins/Losses はその期間の最終値（増分でなく累計値）として保存されているため、
 *  期間最後のバケット値を採用する（累計の差分でなく当時のスナップショット）。 */
const aggregateBuckets = (
  daily: readonly AnalyticsBucket[],
  range: TrendRange,
): readonly AggregatedBucket[] => {
  if (daily.length === 0) return [];
  const keyFn = (b: AnalyticsBucket): string => {
    if (range === 'day') return b.date;
    if (range === 'year') return b.date.slice(0, 4);
    if (range === 'month') return b.date.slice(0, 7);
    // 週: ISO週はサーバーで持っていないので yyyy-Wxx を date から計算
    return isoWeekKey(b.date);
  };
  const groups = new Map<
    string,
    {
      shots: number;
      success: number;
      lastMatchWins: number;
      lastMatchLosses: number;
      lastDate: string;
    }
  >();
  for (const b of daily) {
    const k = keyFn(b);
    const g = groups.get(k) ?? {
      shots: 0,
      success: 0,
      lastMatchWins: 0,
      lastMatchLosses: 0,
      lastDate: '',
    };
    g.shots += b.shots;
    g.success += b.success;
    if (b.date >= g.lastDate) {
      g.lastDate = b.date;
      g.lastMatchWins = b.matchWins;
      g.lastMatchLosses = b.matchLosses;
    }
    groups.set(k, g);
  }
  return [...groups.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([label, g]) => ({
      label,
      shots: g.shots,
      success: g.success,
      matchWins: g.lastMatchWins,
      matchLosses: g.lastMatchLosses,
    }));
};

/** YYYY-MM-DD → ISO 週キー（YYYY-Www）。 */
const isoWeekKey = (ymd: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const date = new Date(Date.UTC(y, mo, d));
  // ISO週: 木曜を含む週
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(
    ((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
  return `${date.getUTCFullYear()}-W${weekNo.toString().padStart(2, '0')}`;
};

/** 集計バケットから SVG 折れ線グラフを構築する（自前・依存なし）。 */
const buildTrendSvg = (data: readonly AggregatedBucket[]): SVGSVGElement => {
  const W = 320;
  const H = 140;
  const PAD = 24;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('class', 'pool-profile-svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', String(H));
  // 軸
  const axis = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  axis.setAttribute(
    'd',
    `M${PAD} ${PAD} L${PAD} ${H - PAD} L${W - PAD} ${H - PAD}`,
  );
  axis.setAttribute('stroke', '#666');
  axis.setAttribute('fill', 'none');
  axis.setAttribute('stroke-width', '1');
  svg.appendChild(axis);
  // データなしは座標なし
  const n = data.length;
  if (n === 0) return svg;
  const xAt = (i: number): number =>
    n === 1 ? (W - PAD * 2) / 2 + PAD : PAD + ((W - PAD * 2) * i) / (n - 1);
  const yAt = (frac: number): number =>
    H - PAD - (H - PAD * 2) * Math.max(0, Math.min(1, frac));
  // 試合勝率
  const winPath = polylinePath(
    data.map((b, i) => {
      const denom = b.matchWins + b.matchLosses;
      const v = denom > 0 ? b.matchWins / denom : 0;
      return [xAt(i), yAt(v)];
    }),
  );
  const winEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  winEl.setAttribute('d', winPath);
  winEl.setAttribute('stroke', '#33d17a');
  winEl.setAttribute('stroke-width', '2');
  winEl.setAttribute('fill', 'none');
  svg.appendChild(winEl);
  // ショット成功率
  const accPath = polylinePath(
    data.map((b, i) => {
      const v = b.shots > 0 ? b.success / b.shots : 0;
      return [xAt(i), yAt(v)];
    }),
  );
  const accEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  accEl.setAttribute('d', accPath);
  accEl.setAttribute('stroke', '#ffd400');
  accEl.setAttribute('stroke-width', '2');
  accEl.setAttribute('fill', 'none');
  svg.appendChild(accEl);
  // y 軸目盛: 50%
  const mid = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  mid.setAttribute('x1', String(PAD));
  mid.setAttribute('x2', String(W - PAD));
  mid.setAttribute('y1', String(yAt(0.5)));
  mid.setAttribute('y2', String(yAt(0.5)));
  mid.setAttribute('stroke', '#333');
  mid.setAttribute('stroke-dasharray', '2 2');
  svg.appendChild(mid);
  return svg;
};

const polylinePath = (pts: ReadonlyArray<readonly [number, number]>): string => {
  if (pts.length === 0) return '';
  let d = `M${pts[0]![0]} ${pts[0]![1]}`;
  for (let i = 1; i < pts.length; i++) d += ` L${pts[i]![0]} ${pts[i]![1]}`;
  return d;
};
